import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Database, Tags, Network, Clapperboard, BarChart3, GaugeCircle, FolderSync, Download } from 'lucide-react';

const features = [
  { icon: Database, title: 'Rekordbox DB Sync', desc: 'Import and export against Rekordbox 6/7 databases while preserving playlists, cues, and metadata.' },
  { icon: Tags, title: 'Quick Tag Mode', desc: 'Run fast file-scoped tagging sessions for folders without changing your Rekordbox workflow.' },
  { icon: Network, title: 'Graph Playlist', desc: 'Visualize energy and key transitions, then save a playable track order to your library.' },
  { icon: Clapperboard, title: 'Chapter Builder', desc: 'Plan structured DJ sets with chapter blocks and export-ready timestamps.' },
  { icon: BarChart3, title: 'Analytics Screen', desc: 'Understand genre, key, BPM, and library mix with charts built for set prep.' },
  { icon: GaugeCircle, title: 'Benchmark Center', desc: 'Run built-in benchmarks to measure scan, sort, and cache performance at scale.' },
  { icon: FolderSync, title: 'Watch Folder', desc: 'Auto-ingest new tracks from a folder and apply enrichment pipelines without manual rescans.' },
  { icon: Download, title: 'Downloader Upgrades', desc: 'Use improved Tidal and Qobuz download queues with cleaner status and settings flows.' },
];

export default function Features() {
  const ref = useRef(null);
  const v = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="features" data-testid="features-section" ref={ref} style={{ padding: 'clamp(120px,14vw,200px) 0' }}>
      <div className="container">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={v ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}
          style={{ textAlign: 'center', maxWidth: 560, margin: '0 auto 72px' }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 500, color: '#52525B', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Features</span>
          <h2 data-testid="features-title" style={{ fontFamily: 'var(--heading)', fontWeight: 800, fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.03em', marginTop: 16 }}>
            Built for real DJ workflows
          </h2>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, background: 'rgba(255,255,255,0.04)', borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.04)' }} className="feat-grid-v3">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div key={i} data-testid={`feature-card-${i}`} initial={{ opacity: 0, y: 20 }} animate={v ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.1 + i * 0.06 }}
                style={{ background: '#09090B', padding: 'clamp(28px,3vw,40px)', transition: 'background .3s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#0E0E10'} onMouseLeave={e => e.currentTarget.style.background = '#09090B'}>
                <Icon size={20} color="#71717A" strokeWidth={1.5} style={{ marginBottom: 20 }} />
                <h3 style={{ fontFamily: 'var(--heading)', fontWeight: 700, fontSize: 16, marginBottom: 8, letterSpacing: '-0.01em', color: '#E4E4E7' }}>{f.title}</h3>
                <p style={{ color: '#52525B', fontSize: 14, lineHeight: 1.7 }}>{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
      <style>{`@media(max-width:900px){.feat-grid-v3{grid-template-columns:1fr !important}}@media(min-width:901px) and (max-width:1100px){.feat-grid-v3{grid-template-columns:repeat(2,1fr) !important}}`}</style>
    </section>
  );
}
